import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import axiosInstance from "../../../utils/axiosInstance";
import { formatDate } from "../../../utils/dateFormatter";
import { API_URL } from "../../../config";
import { useAuth } from "../../../Context/AuthContext";
import "./ViewTrainee.css";

function ViewTrainee() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [trainee, setTrainee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [updatingInterval, setUpdatingInterval] = useState(false);
  const [inactivityMonth, setInactivityMonth] = useState(new Date().getMonth() + 1);
  const [inactivityYear, setInactivityYear] = useState(new Date().getFullYear());
  const [inactivityDailyDate, setInactivityDailyDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const { user: currentUser } = useAuth();

  // Screenshots activity tracker state
  const [screenshots, setScreenshots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // Fetch screenshots when trainee details or selected date change
  useEffect(() => {
    if (!trainee || !trainee._id) return;

    const fetchScreenshots = async () => {
      setLoadingScreenshots(true);
      try {
        const response = await axiosInstance.get(
          `/api/screenshots/employee/${trainee._id}?date=${selectedDate}`
        );
        if (response.data.success) {
          setScreenshots(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching screenshots:", error);
      } finally {
        setLoadingScreenshots(false);
      }
    };

    fetchScreenshots();
  }, [trainee, selectedDate]);

  useEffect(() => {
    const fetchTraineeDetails = async () => {
      try {
        // Fetch this specific trainee directly from DB (ensures latest screenshotInterval/inactivityTimeout)
        const response = await axiosInstance.get(`/api/getEmpDataByID/${employeeId}`);
        const emp = response.data;

        if (emp && emp._id) {
          setTrainee(emp);
        } else {
          alert("Trainee not found!");
          navigate("/admin/trainee");
        }
      } catch (error) {
        console.error("Error fetching trainee:", error);
        alert("Failed to fetch trainee details");
      } finally {
        setLoading(false);
      }
    };

    fetchTraineeDetails();
  }, [employeeId, navigate]);


  const handleGenerateCode = async () => {
    if (!trainee || !trainee._id) return;
    setGeneratingCode(true);
    try {
      const response = await axiosInstance.post("/api/generate-code", {
        employeeId: trainee._id,
      });
      setTrainee({
        ...trainee,
        isLocked: true,
        unlockOTP: response.data.code,
        inactivityLogoutCount: (trainee.inactivityLogoutCount || 0) + 1,
        inactivityHistory: [...(trainee.inactivityHistory || []), new Date().toISOString()]
      });
      alert(`New code generated: ${response.data.code}`);
    } catch (err) {
      console.error("Generate code error:", err);
      const errMsg = err.response?.data?.message || "Failed to generate code";
      alert(errMsg);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleUpdateSettings = async (type, newValue) => {
    if (!trainee || !trainee._id) return;
    setUpdatingInterval(true);
    const updateData = {};
    if (type === "interval") updateData.screenshotInterval = parseInt(newValue);
    if (type === "timeout") updateData.inactivityTimeout = parseInt(newValue);

    try {
      const response = await axiosInstance.put(`/api/updateOfficeTiming/${trainee._id}`, updateData);

      if (response.data.employee) {
        setTrainee(response.data.employee);
      } else {
        setTrainee({
          ...trainee,
          ...updateData
        });
      }
      alert("Settings updated successfully!");
    } catch (err) {
      console.error("Update settings error:", err);
      alert("Failed to update settings");
    } finally {
      setUpdatingInterval(false);
    }
  };

  const handleDeleteScreenshot = async (id) => {
    if (!window.confirm("Are you sure you want to delete this screenshot?")) return;
    try {
      await axiosInstance.delete(`/api/screenshots/${id}`);
      setScreenshots(screenshots.filter((s) => s._id !== id));
      if (selectedScreenshot && selectedScreenshot._id === id) {
        setSelectedScreenshot(null);
      }
    } catch (err) {
      console.error("Delete screenshot error:", err);
      alert("Failed to delete screenshot");
    }
  };

  if (loading) {
    return (
      <div className="vt-state-wrap d-flex justify-content-center align-items-center">
        <div className="spinner-border vt-spinner" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!trainee) {
    return (
      <div className="vt-state-wrap container py-5">
        <div className="alert alert-danger">Trainee not found!</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 view-trainee-page">
      <div className="d-flex justify-content-between align-items-center mb-4 vt-topbar">
        <button onClick={() => navigate("/admin/trainee")} className="btn vt-btn vt-btn-secondary">
          Back to List
        </button>
        <div className="d-flex gap-2 flex-wrap">
          <button
            onClick={() => navigate(`/admin/upDateUder/${trainee.employeeId}`)}
            className="btn vt-btn vt-btn-primary"
          >
            Edit Trainee
          </button>
          <button
            onClick={() => navigate(`/admin/moveToEmplyee/${trainee.employeeId}`)}
            className="btn vt-btn vt-btn-success"
          >
            Move to Employee
          </button>
        </div>
      </div>

      <div className="card shadow-lg border-0 rounded-4 mb-4 vt-main-card">
        <div className="card-header text-center py-4 rounded-top-4 vt-hero">
          <h3 className="mb-0 fw-bold">Trainee/Intern Profile</h3>
        </div>

        <div className="card-body p-4">
          <div className="row mb-4">
            <div className="col-md-3 text-center vt-profile-col">
              {trainee.img ? (
                <img src={trainee.img} alt={trainee.ename} className="rounded-circle border shadow vt-avatar" />
              ) : (
                <div className="rounded-circle text-white d-flex align-items-center justify-content-center mx-auto shadow vt-avatar vt-avatar-fallback">
                  {trainee.ename?.charAt(0).toUpperCase()}
                </div>
              )}
              <h4 className="mt-3 mb-1">{trainee.ename}</h4>
              <p className="text-muted">{trainee.employeeId}</p>
              <span className="badge px-3 py-2 vt-type-badge">{trainee.userType || "Trainee"}</span>
            </div>

            <div className="col-md-9">
              <div className="mb-4">
                <h5 className="vt-section-title mb-3 border-bottom pb-2">Personal Information</h5>
                <div className="row g-3">
                  <div className="col-md-6"><div className="vt-info-item"><strong>Date of Birth:</strong><p className="mb-0">{formatDate(trainee.dateOfBirth)}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Gender:</strong><p className="mb-0">{trainee.gender}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Phone:</strong><p className="mb-0">{trainee.phoneNo}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Personal Email:</strong><p className="mb-0">{trainee.personal_email}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Official Email:</strong><p className="mb-0">{trainee.official_email}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Father's Name:</strong><p className="mb-0">{trainee.fatherName}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Mother's Name:</strong><p className="mb-0">{trainee.motherName}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Address:</strong><p className="mb-0">{trainee.address}</p></div></div>
                </div>
              </div>

              <div className="mb-4">
                <h5 className="vt-section-title mb-3 border-bottom pb-2">Emergency Contact</h5>
                <div className="row g-3">
                  <div className="col-md-6"><div className="vt-info-item"><strong>Contact Number:</strong><p className="mb-0">{trainee.emergencyContact}</p></div></div>
                  <div className="col-md-6"><div className="vt-info-item"><strong>Relationship:</strong><p className="mb-0">{trainee.relation}</p></div></div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Bank Details</h5>
            <div className="row g-3">
              <div className="col-md-4"><div className="vt-info-item"><strong>Bank Name:</strong><p className="mb-0">{trainee.bankName || "N/A"}</p></div></div>
              <div className="col-md-4"><div className="vt-info-item"><strong>Account Number:</strong><p className="mb-0">{trainee.accountNo || "N/A"}</p></div></div>
              <div className="col-md-4"><div className="vt-info-item"><strong>IFSC Code:</strong><p className="mb-0">{trainee.ifscCode || "N/A"}</p></div></div>
              <div className="col-md-6"><div className="vt-info-item"><strong>Bank Address or Branch Name:</strong><p className="mb-0">{trainee.bankAddress || "N/A"}</p></div></div>
              <div className="col-md-6"><div className="vt-info-item"><strong>Account Holder:</strong><p className="mb-0">{trainee.accountHolderName || "N/A"}</p></div></div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Identity Documents</h5>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="vt-info-item">
                  <strong>Aadhaar Card:</strong>
                  <p className="mb-0">{trainee.adarCardNo || "N/A"}</p>
                  {trainee.aadhaarFile && (
                    <a href={trainee.aadhaarFile} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-link p-0 mt-1">
                      📄 View Aadhaar Doc
                    </a>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="vt-info-item">
                  <strong>PAN Number:</strong>
                  <p className="mb-0">{trainee.panNo || "N/A"}</p>
                  {trainee.panFile && (
                    <a href={trainee.panFile} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-link p-0 mt-1">
                      📄 View PAN Doc
                    </a>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="vt-info-item">
                  <strong>Qualification:</strong>
                  <p className="mb-0">{trainee.qualification}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Training Details</h5>
            <div className="row g-3">
              <div className="col-md-3"><div className="vt-info-item"><strong>Department:</strong><p className="mb-0">{trainee.department?.deptName || "N/A"}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Service:</strong><p className="mb-0">{trainee.service?.serviceName || "N/A"}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Last Experience:</strong><p className="mb-0">{trainee.lastExp}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Exp with PWT:</strong><p className="mb-0">{trainee.expWithPWT}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Interview Date:</strong><p className="mb-0">{trainee.interviewDate ? new Date(trainee.interviewDate).toLocaleDateString() : "N/A"}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Joining Date:</strong><p className="mb-0">{trainee.joiningDate ? new Date(trainee.joiningDate).toLocaleDateString() : "N/A"}</p></div></div>
              <div className="col-md-3"><div className="vt-info-item"><strong>Working Time:</strong><p className="mb-0">{trainee.workingTime}</p></div></div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Stipend/Salary Information</h5>
            <div className="row g-3">
              <div className="col-md-6"><div className="vt-info-item"><strong>Expected Salary:</strong><p className="mb-0 vt-salary-warn">Rs {trainee.expectedSalary?.toLocaleString() || "N/A"}</p></div></div>
              <div className="col-md-6"><div className="vt-info-item"><strong>Given Stipend/Salary:</strong><p className="mb-0 vt-salary-success">Rs {trainee.givenSalary?.toLocaleString() || "N/A"}</p></div></div>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Account Security & Monitoring</h5>
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <div className="vt-info-item">
                  <strong>Account Status:</strong>
                  <p className="mb-0">
                    {trainee.isLocked ? (
                      <span className="badge bg-danger">Locked 🔒</span>
                    ) : (
                      <span className="badge bg-success">Active ✅</span>
                    )}
                  </p>
                </div>
              </div>

              <div className="col-md-3 text-center">
                <div className="vt-info-item">
                  <strong>Inactivity Logouts (Monthly):</strong>
                  <div className="d-flex justify-content-center align-items-center gap-1 mt-1 mb-2">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "85px", fontSize: "0.75rem" }}
                      value={inactivityMonth}
                      onChange={(e) => setInactivityMonth(Number(e.target.value))}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", { month: "short" })}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "70px", fontSize: "0.75rem" }}
                      value={inactivityYear}
                      onChange={(e) => setInactivityYear(Number(e.target.value))}
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  <p className="mb-0">
                    <span className="badge bg-warning text-dark">
                      {(() => {
                        const legacyCount = Math.max(0, (trainee.inactivityLogoutCount || 0) - (trainee.inactivityHistory?.length || 0));
                        let cnt = (trainee.inactivityHistory || []).filter(d => {
                          const date = new Date(d);
                          return date.getMonth() + 1 === inactivityMonth && date.getFullYear() === inactivityYear;
                        }).length;
                        if (inactivityMonth === new Date().getMonth() + 1 && inactivityYear === new Date().getFullYear()) {
                          cnt += legacyCount;
                        }
                        return cnt;
                      })()} Attempts
                    </span>
                  </p>
                </div>
              </div>

              <div className="col-md-3 text-center">
                <div className="vt-info-item">
                  <strong>Inactivity Logouts (Daily):</strong>
                  <div className="d-flex justify-content-center align-items-center gap-1 mt-1 mb-2">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      style={{ width: "130px", fontSize: "0.75rem" }}
                      value={inactivityDailyDate}
                      onChange={(e) => setInactivityDailyDate(e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <p className="mb-0">
                    <span className="badge bg-warning text-dark">
                      {(() => {
                        if (!inactivityDailyDate) return 0;
                        const targetDate = new Date(inactivityDailyDate);
                        const cnt = (trainee.inactivityHistory || []).filter(d => {
                          const date = new Date(d);
                          return date.getFullYear() === targetDate.getFullYear() &&
                            date.getMonth() === targetDate.getMonth() &&
                            date.getDate() === targetDate.getDate();
                        }).length;
                        return cnt;
                      })()} Attempts
                    </span>
                  </p>
                </div>
              </div>

              <div className="col-md-3">
                <div className="vt-info-item">
                  <strong>Screenshot Interval:</strong>
                  <select
                    className="form-select form-select-sm mt-1"
                    value={String(trainee.screenshotInterval ?? 300)}
                    onChange={(e) => handleUpdateSettings("interval", e.target.value)}
                    disabled={updatingInterval}
                  >
                    <option value="5">5 Seconds</option>
                    <option value="10">10 Seconds</option>
                    <option value="60">1 Minute</option>
                    <option value="300">5 Minutes</option>
                    <option value="900">15 Minutes</option>
                    <option value="1800">30 Minutes</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="vt-info-item">
                  <strong>Inactivity Timeout:</strong>
                  <select
                    className="form-select form-select-sm mt-1"
                    value={String(trainee.inactivityTimeout ?? 300)}
                    onChange={(e) => handleUpdateSettings("timeout", e.target.value)}
                    disabled={updatingInterval || currentUser?.role !== "superadmin"}
                  >
                    <option value="10">10 Seconds (Testing)</option>
                    <option value="60">1 Minute</option>
                    <option value="300">5 Minutes</option>
                    <option value="600">10 Minutes</option>
                    <option value="1800">30 Minutes</option>
                    <option value="3600">1 Hour</option>
                  </select>
                </div>
              </div>

              <div className="col-md-3 text-end">
                <button
                  className="btn vt-btn vt-btn-primary btn-sm"
                  onClick={handleGenerateCode}
                  disabled={generatingCode}
                >
                  {generatingCode ? "Generating..." : trainee.isLocked ? "Regenerate OTP" : "Lock Account"}
                </button>
              </div>

              {trainee.isLocked && trainee.unlockOTP && (
                <div className="col-12 mt-2">
                  <div className="alert alert-warning mb-0 py-2">
                    <strong>Unlock Code:</strong> <span className="ms-2 fw-bold text-dark">{trainee.unlockOTP}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <h5 className="vt-section-title mb-3 border-bottom pb-2">Documents</h5>
            <div className="row g-3">
              <div className="col-md-12">
                {trainee.resumeFile ? (
                  <a
                    href={trainee.resumeFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn vt-btn vt-btn-secondary"
                  >
                    Download Resume
                  </a>
                ) : (
                  <span className="text-muted">No Resume Available</span>
                )}
              </div>
            </div>
          </div>

          {/* 🎥 Screen Activity Tracker Timeline */}
          <div className="mb-4 vt-activity-section border-top pt-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <h5 className="vt-section-title mb-2">🎥 Screen Activity Timeline (Last 15 Days)</h5>
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-muted small">Select Date:</span>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="form-control form-control-sm vt-date-picker"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {loadingScreenshots ? (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-secondary" role="status">
                  <span className="visually-hidden">Loading screenshots...</span>
                </div>
              </div>
            ) : screenshots.length > 0 ? (
              <div className="row g-3 vt-screenshots-grid">
                {screenshots.map((s, index) => (
                  <div key={index} className="col-sm-6 col-md-4 col-lg-3">
                    <div
                      className="card h-100 vt-screenshot-card border shadow-sm"
                      onClick={() => setSelectedScreenshot(s)}
                    >
                      <div className="vt-screenshot-img-wrapper position-relative">
                        <img
                          src={s.imageBuffer}
                          alt={`Captured at ${new Date(s.createdAt).toLocaleTimeString()}`}
                          className="card-img-top vt-screenshot-thumb"
                        />
                        <div className="vt-screenshot-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gap-2">
                          <span className="btn btn-sm btn-light shadow-sm" onClick={(e) => { e.stopPropagation(); setSelectedScreenshot(s); }}>
                            View
                          </span>
                          <button
                            className="btn btn-sm btn-danger shadow-sm"
                            onClick={(e) => { e.stopPropagation(); handleDeleteScreenshot(s._id); }}
                            title="Delete Screenshot"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="card-body p-2 d-flex flex-column justify-content-between" onClick={() => setSelectedScreenshot(s)}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="badge bg-secondary text-light vt-time-badge">
                            {new Date(s.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="badge bg-success-subtle text-success vt-status-badge">
                            Active 🟢
                          </span>
                        </div>
                        <div className="text-muted small text-truncate" title={s.currentRoute || "/"}>
                          Route: <strong>{s.currentRoute || "/"}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-light text-center py-4 border rounded-3 vt-empty-alert">
                No activity screenshots recorded for this date.
                <br />
                <span className="text-muted small">Note: Captures auto-expire after 15 days, or the trainee/intern may have been offline / idle.</span>
              </div>
            )}
          </div>

          {/* 🔍 Zoom / Lightbox Full Screen Modal */}
          {selectedScreenshot && (
            <div
              className="modal fade show d-block vt-lightbox-modal"
              tabIndex="-1"
              onClick={() => setSelectedScreenshot(null)}
            >
              <div className="modal-dialog modal-dialog-centered modal-xl" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                  <div className="modal-header bg-dark text-white border-0 py-2 px-3 d-flex justify-content-between align-items-center">
                    <h6 className="modal-title mb-0 fw-bold">
                      📸 captured: {new Date(selectedScreenshot.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })} (Route: {selectedScreenshot.currentRoute || "/"})
                    </h6>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setSelectedScreenshot(null)}
                    ></button>
                  </div>
                  <div className="modal-body p-0 bg-secondary-subtle text-center">
                    <img
                      src={selectedScreenshot.imageBuffer}
                      alt="Full screen capture"
                      className="img-fluid vt-screenshot-full"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewTrainee;
